<template>
  <div id="Studio-container">

    <div class="announcement-container" v-if="UI.isAnnouncementVisible">
      <div class="announcement-panel">
        <header>
          <img src="/img/logo-1.png" />
          <span>Creek Studio Preview</span>
        </header>
        <main>
          <p><b>Creek Studio</b> is in development, but here is an early preview!</p>
          <p>It combines <b>automation</b> and <b>live DJs</b> &mdash; so it plays shows 24/7 <b>and</b> allows DJs to take control, build playlists, or upload their prerecorded shows.</p>
          <!-- <p>Plus, it runs on its own hardware device inside your studio, called the <a href="https://creek.org/broadcaster">Creek Broadcaster</a>.</p> -->
          <h3>Try these:</h3>
          <ol>
            <li><b>Listen</b> to the stream.</li>
            <li><b>Switch</b> to the Classical station (click <b>Demo Station</b>).</li>
            <!-- <li></li> -->
            <li><b>Find</b> the <span class="highlight">current song</span> (in yellow).</li>
            <!-- <li class="soon"><b>Coming soon:</b> Edit content.</li> -->
          </ol>
          <!-- <p>For more updates, see the <a target="_blank" href="https://blog.creek.org">Creek Blog</a>.</p> -->
        </main>
        <footer>  
          <button class="button" @click="UI.isAnnouncementVisible=0">
            <b>Okay!</b> Show me the preview.
          </button>
        </footer>
      </div>
    </div>

    <header class="toolbar" :class="{'device-loaded': remoteControl.target}">

      <div class="header-inner">

        <!-- Logo -->
        <!--
        <section class="float-left image">
          <img class="logo" :src="assets.img.logo">
        </section>
        -->

        <!-- Loading -->
        <section class="button float-left toolbar-loading" v-if="remoteControl.target && (!instanceCurrent || !instanceCurrent.timeline)">
          <button class="button-clear">
            <font-awesome-icon :icon="['fad', 'circle-notch']" spin></font-awesome-icon>
            Loading
          </button>
        </section>

        <!-- Studio -->
        <section class="float-left toolbar-studio" v-if="instanceCurrent && instanceCurrent.timeline">
          <v-popover :placement="'bottom'" :popover-class="['in-toolbar']"> 
            <button class="button">
              <font-awesome-icon :icon="['far', 'bars']"></font-awesome-icon> Studio
            </button>
            <template slot="popover" :class="'in-toolbar'">
              <ul class="popover-list">
                <li>
                  <button class="btn">Timeline</button>
                </li>
                <li>
                  <button class="btn">Shows</button> <button class="btn">Posts</button>
                </li>
                <li>
                  <button class="btn"">Settings</button>
                </li>
                <!--
                <li>
                  <button class="btn" @click="pageShow('timeline')">Timeline</button>
                </li>
                <li>
                  <button class="btn" @click="pageShow('log-viewer')">Log Viewer</button>
                </li>
                <li>
                  <button class="btn" @click="pageShow('settings')">Settings</button>
                </li>
                -->
              </ul>
            </template>
          </v-popover> 
        </section>

        <!-- Date -->
        <section class="float-left" v-if="instanceCurrent && instanceCurrent.timeline">
          <v-popover :placement="'bottom'" :popover-class="['in-toolbar']"> 
            <button class="button">
            <!-- <button class="button-unclickable"> -->
              <font-awesome-icon :icon="['far', 'calendar']"></font-awesome-icon>
              <span class="label" v-text="momentTimezone(instanceCurrent.timeline.dateStart, 'YYYY-MM-DD')"></span>
            </button>
            <template slot="popover">
              <datepicker :disabled-dates="{from: new Date(2019, 8, 29)}" :value="timelineDate" :inline="true" @selected="dateSelected"></datepicker>
            </template>
          </v-popover> 
        </section>

        <!-- Center -->
        <section class="float-left toolbar-center" v-if="instanceCurrent && instanceCurrent.timeline">
          <button class="button" @click="center()">
            <span class="clock-main" v-text="clocks.actual"></span>
            <font-awesome-icon :icon="['far', 'crosshairs']"></font-awesome-icon>
          </button>
        </section>

        <!-- Play/Stop -->
        <section class="float-left toolbar-auto" :class="{ loading: UI.loadingControls.playStop, 'audio-is-playing': instanceCurrent && instanceCurrent.status && instanceCurrent.status.playing && !UI.loadingControls.playStop }" v-if="instanceCurrent && instanceCurrent.timeline">
          <button class="button" @click="play()" v-if="instanceCurrent && instanceCurrent.status && !instanceCurrent.status.playing">
            <font-awesome-icon v-if="UI.loadingControls.playStop" :icon="['far', 'circle-notch']" spin></font-awesome-icon>
            <font-awesome-icon v-if="!UI.loadingControls.playStop" :icon="['far', 'play']"></font-awesome-icon>
            Auto
          </button>
          <button class="button" @click="stop()" v-if="instanceCurrent && instanceCurrent.status && instanceCurrent.status.playing">
            <font-awesome-icon v-if="UI.loadingControls.playStop" :icon="['far', 'circle-notch']" spin></font-awesome-icon>
            <font-awesome-icon v-if="!UI.loadingControls.playStop" :icon="['far', 'pause']"></font-awesome-icon>
            Auto
          </button>
        </section>

        <!-- User -->
        <section class="float-right toolbar-user" v-if="instanceCurrent && instanceCurrent.timeline">
          <v-popover :placement="'bottom'" :popover-class="['in-toolbar']"> 
            <button class="button">
              <font-awesome-icon :icon="['far', 'user']"></font-awesome-icon>
              <span v-text="demo.user.display_name"></span>
            </button>
            <template slot="popover">
              <ul class="popover-list">
                <li>
                  <button v-close-popover class="btn">Profile</button>
                </li>
                <li>
                  <button v-close-popover class="btn">Sign out</button>
                </li>
              </ul>
            </template>
          </v-popover> 
        </section>

        <!-- Notifications -->
        <section class="float-right toolbar-notifications" v-if="instanceCurrent && instanceCurrent.timeline">
          <v-popover :placement="'bottom'" :popover-class="['in-toolbar']"> 
            <button class="button">
              <font-awesome-icon :icon="['far', 'bell']"></font-awesome-icon>
            </button>
            <template slot="popover">
              <ul class="popover-list notifications">
                <li v-for="n in demo.notifications">
                  <div class="text" v-text="n.text"></div>
                  <div class="footer">
                    <span class="type" v-text="n.type"></span>
                    <span class="date" v-text="$moment(n.date).format('H:mma, M/D')"></span>
                  </div>
                </li>
              </ul>
            </template>
          </v-popover> 
        </section>

        <!-- Instance Switcher -->
        <section class="float-right toolbar-instance" v-if="instanceCurrent && instanceCurrent.timeline">
          <v-popover :placement="'bottom'" :class="'in-toolbar'" :popover-class="['in-toolbar']"> 
            <button class="button">
              <font-awesome-icon :icon="['fas', 'broadcast-tower']"></font-awesome-icon>
              <span v-if="instanceCurrent.settings && instanceCurrent.settings.main && instanceCurrent.settings.main.title" v-text="demoZoneTitle(instanceCurrent.settings.main.title)"></span>
            </button>
            <template slot="popover" :class="'in-toolbar'">
              <ul class="popover-list instance-switcher">
                <li v-for="z in API.instances">
                  <button 
                    class="btn" 
                    v-if="z.settings.main.title" 
                    v-text="demoZoneTitle(z.settings.main.title)" 
                    @click="instanceSwitchView(z.name)"
                    v-close-popover 
                  ></button>
                </li>
              </ul>
            </template>
          </v-popover> 
        </section>

        <!-- Peak Meter -->
        <section class="float-left" v-if="instanceCurrent && instanceCurrent.timeline">
          <peak-meter :instance="instanceCurrent" :config="config"></peak-meter>
        </section>

        <!-- Listen -->
        <section class="float-left" v-if="remoteControl.target && remoteControl.target.address && instanceCurrent && instanceCurrent.timeline">
          <stream-player :instance="this.instanceCurrent" :stream-address="this.instanceCurrent.streamAddress" :addresses="this.config.addresses"></stream-player>
        </section>

      </div>

    </header>

    <div class="header-spacer"></div>

    <div class="main-container">

      <div class="pages-container" v-if="API.instances !== null">

        <div v-if="UI.page=='timeline'" class="timeline-container">
          <page-timeline v-bind:key="Instance.name" v-for="Instance in API.instances" v-if="Instance.name == instanceName" :config="config" :instance="Instance" :id="'Timeline-'+Instance.name" v-on:refresh="refreshAPI()"></page-timeline>
        </div>

        <page-media-library v-if="UI.page=='media-library'" :config="config" :api-data="API" :instance="instanceCurrent" v-on:refresh="refreshAPI()"></page-media-library>
        
        <page-playlist-editor v-if="UI.page=='playlists'" :config="config" :api-data="API" :instance="instanceCurrent" v-on:refresh="refreshAPI()"></page-playlist-editor>

        <page-file-browser v-if="UI.page=='file-browser'" :config="config" :api-data="API" :instance="instanceCurrent" v-on:refresh="refreshAPI()"></page-file-browser>

        <!-- <page-settings v-if="UI.page=='settings'" :config="config" :api-data="API" :instance="instanceCurrent" :remote-control-target="remoteControl.target" v-on:refresh="refreshAPI()"></page-settings> -->
        
        <!-- <page-log-viewer :logs="logs" v-if="UI.page=='log-viewer'"></page-log-viewer> -->

      </div>

      <div class="sidebar-container" v-if="API.instances !== null">
        <div id="Sidebar">
          <section>
            <!-- <h4>Content</h4> -->
            <v-popover placement="bottom"> 
              <button class="button button-create-new">
                <font-awesome-icon :icon="['fas', 'plus']"></font-awesome-icon>
                Create New
              </button>
              <template slot="popover">
                <ul class="popover-list">
                  <li>
                    <button class="btn">Show</button>
                  </li>
                  <li>
                    <button class="btn"">Broadcast</button>
                  </li>
                  <li>
                    <button class="btn"">Post</button>
                  </li>
                </ul>
              </template>
            </v-popover>
          </section>
          <section>
            <h4>Current</h4>
            <div class="current-track">
              <div class="artist">
                <span class="label">Artist</span>
                <span v-text="$v(instanceCurrent, 'status.current.track.item.artist') || 'Unknown'"></span>
              </div>
              <!-- <span></span> -->
              <div class="title">
                <span class="label">Title</span>
                <span v-text="$v(instanceCurrent, 'status.current.track.item.title') || 'Untitled'"></span>
              </div>
            </div>
          </section>
          <section>
            <h4>Stream</h4>
            <div><b>34</b> Listeners</div>
          </section>
          <section>
            <h4>Messages</h4>
            <div class="message" v-for="message in demo.messages">
              <div class="text" v-text="message.text"></div>
              <div class="footer">
                <span class="from" v-text="message.from"></span>
                <span class="date" v-text="$moment(message.date).format('H:mma, M/D')"></span>
              </div>
            </div>
            <a class="more-link" href="/#">All messages &gt;</a>
          </section>
          <section>
            <h4>Chat</h4>
            <div class="message" v-for="message in demo.chat">
              <div class="text" v-text="message.text"></div>
              <div class="footer">
                <span class="from" v-text="message.from"></span>
                <span class="date" v-text="$moment(message.date).format('H:mma, M/D')"></span>
              </div>
            </div>
            <a class="more-link" href="/#">All chat &gt;</a>
          </section>
        </div>
      </div>


    </div>

    <div class="footer-spacer"></div>
    
  </div>
</template>

<script>

/* eslint-disable */

import axios from 'axios';
import $ from 'jquery';
import _ from 'lodash';
import moment from 'moment-timezone';


// import moment from 'moment';
// var moment = require('moment-timezone');


// Components
import PeakMeter from '@/components/PeakMeter';
import Modal from '@/components/Modal';
import Datepicker from 'vuejs-datepicker';


import { VPopover } from 'v-tooltip'


// Pages
import Timeline from '@/components/Timeline';
import LogViewer from '@/components/LogViewer';
import SettingsEditor from '@/components/SettingsEditor';

import StreamPlayer from '@/components/StreamPlayer';
import RemoteControl from '@/components/RemoteControl';

// Icons

import { library } from '@fortawesome/fontawesome-svg-core'

import { 
  faCircleNotch as fadCircleNotch, 
  faSignIn as fadSignIn, 
  faSignOut as fadSignOut,  
  faArrowToRight as fadArrowToRight,  
  faArrowFromRight as fadArrowFromRight, 
  faBell as fadBell,  
} from '@fortawesome/pro-duotone-svg-icons'

import { 
  faBars, faCalendar, faCircleNotch, faClone, faCrosshairs, faList, faPlay, faPlayCircle, faStepForward, faStop, faPause, faBell, faUser, faBroadcastTower, faPlus,
  faCheckSquare, faSquare,
  faArrowToRight, faArrowFromRight,
} from '@fortawesome/pro-regular-svg-icons'

import { 
  faBell as fasBell, 
  faPlus as fasPlus, 
  faCheckSquare as fasCheckSquare, 
  faSquare as fasSquare,
  faTimes as fasTimes,
  faArrowToRight as fasArrowToRight,  
  faArrowFromRight as fasArrowFromRight, 
  faBroadcastTower as fasBroadcastTower, 
  faUser as fasUser,
} from '@fortawesome/pro-solid-svg-icons'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

library.add(
  faBars, faCalendar, 
  faCircleNotch, fadCircleNotch,
  faClone, faCrosshairs, faList, faPlay, faPlayCircle, faStepForward, faStop, faPause, faBell, faUser, faBroadcastTower, faPlus,

  fasBroadcastTower, fasBell, fasUser, fasPlus,

  fadSignIn, fadSignOut, 
  faCheckSquare, faSquare,
  fasCheckSquare, fasSquare,
  fasTimes,
  fadArrowToRight, fadArrowFromRight, 
  fasArrowToRight, fasArrowFromRight, 
  faArrowToRight, faArrowFromRight,
)

export default {
  name: 'Creek-Studio',
  components: {
    
    'page-timeline': Timeline,
    'page-log-viewer': LogViewer,
    'page-settings': SettingsEditor,
    
    'stream-player': StreamPlayer,
    'peak-meter': PeakMeter,

    'datepicker': Datepicker,
    'font-awesome-icon': FontAwesomeIcon,
    'modal': Modal,
    'v-popover': VPopover,

  },
  data() {
    return {
      demo: { 
        user: {
          display_name: "John Demo"
        },
        notifications:[
          {
            type: 'message',
            text: 'Hey, did you get that thing I sent you?',
            date: "2020-04-03T15:31:50Z",
          },
          {
            type: 'archive',
            text: 'Your archive has posted.',
            date: "2020-04-03T15:31:50Z",
          }
        ],
        messages: [
          {
            text: "Hey, I won't make it into the studio tonight. Just play the show that I uploaded?",
            from: 'Jennifer Demoperson',
            date: "2020-04-03T15:31:50Z",
          }
        ],
        chat: [
          {
            text: "Nice song! Brings back memories.",
            from: 'Frank Demoperson',
            date: "2020-04-02T17:12:14Z",
          }
        ],
      },
      defaults: {
        timezone: "America/Los_Angeles",
      },
      config: {
        address: null,
        password: null,
        vpn_client_address: null,
      },
      remoteControl: {
        target: null,
      },
      defaultTarget: {
        profile: {
          vpn_client_address: '',
          unique_id: 'travis-3'
        }
      },
      API: {
        settings: null,
        instances: null,
      },
      logs: [],
      UI: {
        showMenu: false,
        page: "timeline",
        trackPosition: {},
        currentTrackPosition: null,
        loadingControls: {
          playStop: null,
          skip: null
        },
        seeAllScheduledCommands: false,
        isAnnouncementVisible: true,
      },
      clocks: {
        timezone: null,
        actual: null,
        currentTrackPosition: null,
        currentTrackRemaining: null,
      },
      intervals: {
        refreshAPI: null,
        getCurrentClocks: null,
      },
      instanceName: 'zone-2',
      // instanceName: 'instance-1',
      instanceCurrent: null,
      socket: {
        address: null,
        connection: {},
      },
    };
  },
  computed: {
    upcomingScheduledCommands(){

      let sc = this.instanceCurrent.timeline.scheduledCommands;
      
      if(sc){

        let upcoming = sc.filter(c => 
          c.date && moment(c.date).isAfter(moment())
        )

        return upcoming
      
      }

      return []

    },
    disabledDates(){
      return {
        // to: moment(this.dateSelected).subtract(1, 'day').toDate(),
        from: new Date(2019, 8, 29),
        // from: moment(this.dateSelected).add(1, 'day').toDate(),
        includeDisabled: true // Highlight disabled dates
      }
    },
  },
  methods: {
    demoZoneTitle(string){
      return string
        .replace('Zone 1', 'Classical Station')
        .replace('Zone 2', 'Demo Station')
    },
    timeFormat(seconds) {

      // console.log(seconds)

      if (!seconds || typeof seconds === 'undefined') {
        return '';
      }

      const date = new Date(null);
      date.setSeconds(seconds);
      const time = date.toISOString().substr(11, 8);
      let timeTrimmed = time;
      let trimLimit = 0;

      // Remove leading zeroes
      while ((timeTrimmed.substr(0, 1) === '0' || timeTrimmed.substr(0, 1) === ':') && trimLimit <= 3) {
        timeTrimmed = timeTrimmed.slice(1);
        trimLimit += 1;
      }

      return timeTrimmed;
    },
    pageShow(page){
      this.UI.showMenu = false
      this.UI.page = page;
      this.scrollToTop()
    },
    toggleMenu(){
      this.UI.showMenu = this.UI.showMenu ? false : true;
    },
    toggleAllScheduledCommands(){
      this.UI.seeAllScheduledCommands = this.UI.seeAllScheduledCommands ? false : true
    },
    instanceSwitchView(instanceName) {
      
      this.instanceName = instanceName
      
      this.instanceUpdate()
      
      this.centerInstantly()

    },
    instanceUpdate() {
      
      if(this.API.instances && this.remoteControl.target){
      
        // console.log("Update Instance: " + this.instanceName)
        
        this.instanceCurrent = this.API.instances[this.instanceName];

        // Select the instanceCurrent's date.
        this.dateSelected()

        if(
             this.remoteControl.target 
          && this.remoteControl.target.profile 
          && this.remoteControl.target.profile.unique_id
          && this.instanceCurrent
        ){
          this.instanceCurrent.streamAddress = 'https://listen.creek.org/' 
            + this.remoteControl.target.profile.unique_id + '-' + this.instanceName
        }

      }else{
        this.instanceCurrent = null
      }

    },
    updateRemoteControlTarget(target) {

      this.instanceCurrent = null;
      this.remoteControl.target = null;

      this.pageShow('timeline')

      this.updateRemoteControlTargetAddress(target)

      this.remoteControl.target = target;

      this.API.settings = null
      this.API.instances = null

      this.$nextTick(() => {
        this.centerInstantly()
      });

      var parentThis = this;

      // TODO: Remove this?
      setTimeout(function(){
        parentThis.centerInstantly()
      }, 3000);


      // Socket.io

      // Close existing Socket.io connection.
      if(this.socket.connection.close){
        // console.log("closing existing connection")
        this.socket.connection.close()
      }

      // TODO: Target: Make this dynamic.
      this.socket.address = `wss://demo-a3-automation-relay-1.creek.org`

      // this.socket.address = `${target.profile.vpn_client_address}:3080`

      this.socket.connection = this.$socketIO(
        this.socket.address, 
        {
          transports: ['websocket'],
        }
      )
      
      // console.log("connected probably yeah")

      this.socket.connection.on('app', (app) => {
        
        // console.log("app received")
        console.log(app)
        
        this.API.instances = app.instances
        this.API.settings = app.settings

        parentThis.instanceUpdate()

      })

      this.socket.connection.on('log', (log) => {
        // console.log("log received")
        // this.logs.push(log)
        this.logs.unshift(log)
      })

      this.socket.connection.on('change', (changed) => {
        
        console.log("ws updated:")
        // console.log(changed)

        _.set(
          this.API.instances[changed.instanceName],
          changed.path, 
          changed.value
        )

        parentThis.$forceUpdate()

      })


    },

    removeRemoteControlTarget() {

      this.instanceCurrent = null;
      this.remoteControl.target = null;

      this.API.settings = null
      this.API.instances = null

    },

    showDevices(){


    },

    updateRemoteControlTargetAddress(target) {

      if(!target){
        this.config.address = null;
        this.config.addresses = null;
        this.config.password = null;
        this.config.vpn_client_address = null;
      }

      const addressWithDashes = target.profile.vpn_client_address.replace(/\./g, '-')

      target.address = `http://${target.profile.vpn_client_address}:3000`;

      // TODO: Target: Make this dynamic.
      target.addresses = {
        // automationServer: `http://${target.profile.vpn_client_address}:3000`,
        // audioServer: `http://${target.profile.vpn_client_address}:1234`,
        // libraryClient: `http://${target.profile.vpn_client_address}:3039`,0`,
        automationServer: `https://demo-a1-automation-relay-1.creek.org/`,
        audioServer: `https://demo-a2-automation-relay-1.creek.org/`,
        libraryClient: `https://demo-a4-automation-relay-1.creek.org/`,
      }

      this.config.address = target.address || null;
      this.config.addresses = target.addresses || null;
      this.config.password = target.profile.password || null;
      this.config.vpn_client_address = target.profile.vpn_client_address || null;

    },
    startAuto() {
      var parentThis = this;
      axios
        .post(this.config.address + '/controls/start-auto', {i: this.instanceCurrent.name})
        .then((response) => {
          parentThis.refreshAPI();
        })
        .catch((error) => {
          // console.log(error);
        });
    },
    play() {
      var parentThis = this;
      return
      this.UI.loadingControls.playStop = true
      // this.instanceCurrent.status.playing = true
      axios
        .post(this.config.address + '/controls/start', {i: this.instanceCurrent.name})
        .then((response) => {
          parentThis.refreshAPI(()=>{
            parentThis.UI.loadingControls.playStop = false
          });
        })
        .catch((error) => {
          // console.log(error);
        });
    },
    stop() {
      var parentThis = this;
      return
      // this.instanceCurrent.status.playing = false
      this.UI.loadingControls.playStop = true
      axios
        .post(this.config.address + '/controls/stop', {i: this.instanceCurrent.name})
        .then((response) => {
          parentThis.refreshAPI(()=>{
            parentThis.UI.loadingControls.playStop = false
          });
        })
        .catch((error) => {
          // console.log(error);
        });
    },
    startNext() {
      this.UI.loadingControls.skip = true
      var parentThis = this;
      axios
        .post(this.config.address + '/controls/next', {i: this.instanceCurrent.name})
        .then((response) => {
          parentThis.refreshAPI(()=>{
            parentThis.UI.loadingControls.skip = false
          });
        })
        .catch((error) => {
          // console.log(error);
        });
    },
    refreshAPI(callback = null){
      
      if(!callback){
        callback = function(){}
      }

      // Disable if websocket is active.
      if(this.socket.connection.connected){
        // console.log("Socket connected. Disabling API refresh interval.")
        callback()
        return
      }
      
      return

      // console.log("REFRESHING");
      
      var parentThis = this;
      
      // Don't refresh if no address selected
      if(!this.config.address){
        return;
      }
      
      axios
        .get(this.config.address + '/')
        .then((response) => {
          parentThis.API = response.data.data;
          parentThis.instanceUpdate()
          callback()
        })
        .catch((error) => {
          // console.log(error);
        });

    },
    updateTimeline(callback){
      
      var parentThis = this;

      if(!this.instanceCurrent){
        return;
      }

      if(!this.config.address){
        return;
      }

      // Set the date at the Automation Server
      axios
        .post(this.config.address + '/timeline/date', {
          i: this.instanceCurrent.name, 
          date: moment().format('YYYY-MM-DD')
        })
        .then((response) => {
          callback();
          parentThis.refreshAPI();
        })
        .catch((error) => {
          // console.log(error);
        });

    }, 
    center(){

      this.centerInstantly()

      // var scrollTo =  $(".status-scheduled", document).position().top - 200

      // $('#Studio-container').animate({
      //     scrollTop: scrollTo
      // }, 1000)
    
    },
    centerInstantly(){

      const s = ".status-playing, .status-scheduled"

      this.$nextTick(() => {

        if($(s, document).length > 0){
          var scrollTo =  $(s, document).position().top - 200
        }
        $('#Studio-container').animate({
            scrollTop: scrollTo
        }, 1)
      
      });

    },
    scrollToTop(){

      this.$nextTick(() => {
        
        $('#Studio-container').animate({
            scrollTop: 0
        }, 1)
      
      });

    },
    getCurrentClocks(){

      // Check for current song.
      // Get the remaining time: 
      //   currentTimeRemaining = track.timelineDuration - (track.timelineStart.milliseconds - currentDeviceTime.milliseconds)


      // console.log("Getting clocks.")

      // Only get clocks if there is a current instance selected.
      if(!this.instanceCurrent){
        return
      }

      // Make sure that timezone is set.
      this.getTimezone()

      let timeStringCurrent = moment().format()

      if(this.instanceCurrent.status.time){
        timeStringCurrent = this.instanceCurrent.status.time.utc
      }

      var parentThis = this;
      
      // Don't load if no address selected
      // if(!this.config.address){
      //   return
      // }

      // Make sure that track is available.
      // if(!_.get(this.API.instances[this.instanceName], 'status.track')){
      //   return
      // }


      // parentThis.UI.trackPosition = ;

      parentThis.clocks.actual = this.momentTimezone(timeStringCurrent, 'HH:mm:ss')



      const currentTrackDuration = parentThis.API.instances[this.instanceName].status.current.track.timelineDuration - parentThis.UI.trackPosition

      parentThis.clocks.currentTrackDuration = this.momentTimezone(
        currentTrackDuration,
        'HH:mm:ss'
      )

      // console.log(timelinePlayerName)
      // console.log(parentThis.clocks.currentTrackDuration)
      // console.log(parentThis.UI.trackPosition)
      // console.log(parentThis.UI.trackPosition1)
      // console.log(parentThis.UI.trackPosition2)

      // Remaining
      parentThis.clocks.currentTrackRemaining = parentThis.timeFormat(parentThis.API.instances[this.instanceName].status.current.track.item.duration - parentThis.UI.trackPosition)
      
      // Position
      parentThis.clocks.currentTrackPosition = parentThis.timeFormat(parentThis.API.instances[this.instanceName].status.current.track.item.duration )


    },
    momentTimezone(timeString, formatString){
      return this.$moment(timeString).format(formatString)
    },
    dateSelected(date){
      if(!date && this.instanceCurrent){
        date = this.instanceCurrent.timeline.dateStart
      }
      // console.log("DATE")
      // console.log(date)
      this.timelineDate = date;
    },
    getTimezone(){

      // Only get clocks if these is a current instance selected.
      if(!this.instanceCurrent){
        return
      }

      // Set default
      this.clocks.timezone = this.defaults.timezone

      // Set timezone from API data if available.
      if(this.instanceCurrent.status.time && this.instanceCurrent.status.time.timezone){
        this.clocks.timezone = this.instanceCurrent.status.time.timezone
      }

      moment.tz.setDefault(this.clocks.timezone)

      // Set global timezone on Vue instance.
      this.$moment.tz.setDefault(this.clocks.timezone)

    },
    dateLoad(){
      
      // Set the date at the Automation Server
      axios
        .patch(this.config.address + '/timeline', {
          i: this.instanceCurrent.name, 
          date: moment().format('YYYY-MM-DD')
          // date: moment(this.timelineDate).format('YYYY-MM-DD')
        })
        .then((response) => {
          parentThis.refreshAPI();
        })
        .catch((error) => {
          // console.log(error);
        });

    }, 
    dateScan(date){
      
      let parentThis = this

      // Set the date at the Automation Server
      axios
        .post(this.config.address + '/scan/playlists', {
          i: this.instanceCurrent.name, 
          startDate: moment(this.instanceCurrent.timeline.dateStart),
          endDate: moment(this.instanceCurrent.timeline.dateEnd)
        })
        .then((response) => {
          parentThis.refreshAPI();
        })
        .catch((error) => {
          console.log(error);
        });
    },
  },
  created() {

    this.refreshAPI(function(){
      
      this.centerInstantly()

      this.$nextTick(() => {
        this.centerInstantly()
      });
    
    });

    this.intervals.refreshAPI = setInterval(this.refreshAPI, 2000);
    this.intervals.getCurrentClocks = setInterval(this.getCurrentClocks, 1000);
    
    this.getCurrentClocks();


    this.updateRemoteControlTarget(this.defaultTarget)


    // Set the date for the Date Picker.
    this.dateSelected()

    this.instanceSwitchView(this.instanceName)

    this.$nextTick(() => {
      this.centerInstantly()
    });

  },
};

</script>

<style lang="stylus">


// background-color-1 = #f5f3ea
// background-color-1 = #eae9e3
background-color-1 = #eae9e1
fixedWidthFonts = "Consolas", "Menlo", "Droid Sans Mono", "Lucida Console", "Courier New"

body
  margin 0
  padding 0
  // height 100%
  // width 100%
  // position absolute

html, body
  // border-radius 10px
  overflow hidden

button:focus, button:active
  outline none

.Loading
  padding 40px 20px 20px 20px
  background #777
  color #fff
  margin-top -20px

b.current-target
  font-size 16px !important
  // float left
  font-weight normal
  border 2px solid rgba(76, 152, 74, 0.5)
  color #4c984a
  padding 6px 12px 5px 12px !important
  display block !important
  overflow hidden !important
  max-height 28px
  border-radius 6px
  margin 5px 0 0 0 !important
  line-height 200% 
  
  span
    white-space nowrap

.announcement-container
  width 100%
  height 100%
  z-index 1001
  display flex
  justify-content center
  align-items center
  position fixed
  background rgba(0,0,0,0.5)
  overflow scroll
  top 0
  left 0
  
.announcement-panel
  max-width 600px
  background background-color-1
  box-shadow 0 8px 30px rgba(0,0,0,.4)
  padding 0px
  border-radius 10px
  height auto
  overflow scroll
  margin 0 10px
  header
    padding 18px 18px
    display flex
    align-items center
    img
      // display inline-block
      height 30px
      width auto
      // width 150px
      // height auto
      // vertical-align middle
      margin-right 20px
    span
      font-size 20px
      // line-height 34px
      font-weight 600
      color rgba(0,0,0,0.7)
      margin-top 6px
      // display block
  main
    background #fff
    box-shadow 0 1px 4px rgba(0,0,0,.2)
    // margin 0 20px
    // border-radius 6px
    padding 20px 20px 16px 20px
    line-height 150%
    
    .highlight
      display inline-block
      background #ffa
      border 1px solid rgba(0,0,0,.05)
      border-bottom 2px solid rgba(0,0,0,.1)
      padding 2px 6px
      border-radius 4px
      
    p
      margin 0 0 10px 0
      
    h3
      font-size 17px
      display inline-block
      margin 6px 0 10px 0
      // background #ffa
      // border 1px solid rgba(0,0,0,.05)
      // border-bottom 2px solid rgba(0,0,0,.1)
      // padding 3px 6px
      // border-radius 3px
      
    ol
      list-style none
      margin 0 8px
      padding 0 0 0 1em
      
      li 
        color #333
        margin 0 0 10px 0
        padding 0
        
      li.soon
        color #555
        
        b
          color #000 
          
    li::before {
      content: "\2022";  /* Add content: \2022 is the CSS Code/unicode for a bullet */
      color: #999; /* Change the color */
      font-weight: bold; /* If you want it to be bold */
      display: inline-block; /* Needed to add space between the bullet and the text */
      width: 1em; /* Also needed for space (tweak if needed) */
      margin-left: -0.5em; /* Also needed for space (tweak if needed) */
    }
  footer
    padding 20px
    button.button
      display block
      width 100%
      box-sizing border-box
      border-color rgba(0,0,0,0.3) !important
      padding 14px 16px !important
      box-shadow 0 3px 10px rgba(0,0,0,0.2)
      // border 2px solid #ffc !important
    
#Menu 
  width 200px
  
  button
    background #fff
    padding 10px 20px
    border 0
    border-radius 0
    font-size 18px
    color #000
    display block
    text-decoration none
    border-bottom 1px solid rgba(0, 0, 0, .1)
    width 100%
    box-sizing border-box

    &:hover 
      background #f6f6f6

    &.switch-device
      background #f6f6f6
      border-top 1px solid #ddd


#Studio-container
  margin 0
  position absolute
  // position relative
  left 0
  top 0
  width 100%
  // height auto
  height 100%
  overflow scroll
  font-family Arial, Helvetica, san-serif, sans

  background-image url('/img/studio-background-1.jpg')
  background-size cover
  background-repeat no-repeat
  background-position center center
  background-color #333

  button.button
    background #fff
    border-radius 6px
    border 1px solid rgba(0,0,0,0.15)
    // border 0
    padding 10px 14px
    font-size 16px
    border-bottom 2px solid rgba(0,0,0,0.2)

    &:hover
      background #fff
      border-color rgba(0,0,0,0.3)
      box-shadow 0 2px 6px rgba(0,0,0,.15);
      cursor pointer

    &:active, &:focus
      outline none

    &.small
      padding 4px 6px
      font-size 14px

    &.button-unclickable
      border: none

      &:hover
        cursor default
    
    svg
      opacity 0.7


  button.button-unclickable
    background #fff
    border-radius 6px
    border 1px solid #ddd
    padding 10px 14px
    font-size 16px
    // border-bottom 2px solid #ccc

    &:hover
      background #fff
      // border-bottom 2px solid #c5c5c5
      // box-shadow 0 1px 6px rgba(0,0,0,.12);
      // cursor pointer

    &:active, &:focus
      outline none
    
    svg
      opacity 0.7

  header.toolbar
    background background-color-1
    // border-bottom 1px solid rgba(0,0,0,.1);
    box-shadow 0 0px 4px rgba(0,0,0,0.3), 0 0px 20px rgba(0,0,0,0.3)
    overflow auto
    position fixed
    // height 100%
    z-index 1000
    width 100%

    .header-inner
      min-width 1080px
      max-width 1200px
      margin 0 auto
      overflow auto

    .logo
      background #fff
      max-height 44px
      width auto
      padding 10px
      margin 0

    section.float-left
      margin-left 12px
      float left
      box-sizing border-box

      button 
        
        span.label
          margin 0 8px    

    section.float-right
      margin-right 12px
      float right
      // border-left 1px solid rgba(0,0,0,.2);

      button 

        span.label
          margin 0 8px

    section
      padding 12px 0

    section.image
      padding 0
      font-size 0px

    section.device-loading
      font-size 20px
      padding 20px
      border none
      // height 100%

    section.button
      padding 0

      &.loading
        button
          background background-color-1 !important
          transition all 0.5s

      button
        padding 18px
        font-size 24px
        color #444
        background #fff
        border 0
        transition all 0.1s

        &:hover
          background background-color-1
          box-shadow 0 1px 12px rgba(0,0,0,.6);
          z-index 100
          position relative

        &:active, &:focus
          outline none

        span.label
          color #999
          text-transform uppercase
          margin 0 8px


    section.audio-is-playing
      
      // background #65a856 !important
      color #fff
      
      button
        color #65a856 !important
        font-weight 700


  .toolbar-center
    font-size 16px
    // button
      // padding 23px 10px !important
      // font-size 16px !important
    .clock-main
      font-family fixedWidthFonts
      margin-right 8px
      font-size 16px
  
  .toolbar-studio
    button
      font-weight 700
    svg
      margin-right 6px
  
  .toolbar-auto
    svg
      margin-right 4px
      
  .toolbar-user
    svg
      margin-right 8px

  .toolbar-instance
    svg
      margin-right 8px
      
  .button-create-new
    svg
      margin-right 4px !important

  .toolbar-loading
    box-shadow 0 0 8px rgba(0,0,0,.2)


  ul.popover-list
    margin 0 !important
    padding 4px 4px 14px 4px !important
    background background-color-1
    border-radius 6px
    li
      padding 0
      margin 0 10px 2px 10px
      text-align left
      
  ul.notifications
    margin 0 !important
    padding 4px !important
    background background-color-1
    border-radius 6px
    li
      margin 8px
      padding 8px 12px
      max-width 200px
      border-radius 4px
      background #fff
      box-shadow 0 2px 4px rgba(0,0,0,.2)
      text-align left
      line-height 150%
      .text
        white-space nowrap
        overflow hidden
        text-overflow ellipsis
        margin-bottom 4px
        font-size 14px
      .footer
        font-size 13px
        .type
          text-transform capitalize
          margin-right 8px
          color #999
        .date
          color #bbb
        
  .header-spacer
    height 63px


  .clearfix:after 
     content " " /* Older browser do not support empty content */
     visibility hidden
     display block
     height 0
     clear both
  

  .Player
    // background #f3f3f3
    // box-shadow 0 2px 12px rgba(0,0,0,0.2)
    // border-radius 6px
    // border 1px solid rgba(0,0,0,.2)
    overflow auto
    // max-width 950px
    margin 0 auto
    
    width 100%
    // height 100%
    // height auto
    position relative
    
    // padding-top 70px


    button.button-clear
      border 0
      background transparent






.tooltip 
  display block !important
  z-index 10000
  font-family Arial, Helvetica, sans-serif, sans
  font-size 14px


.tooltip .tooltip-inner 
  background black
  color white
  border-radius 16px
  padding 5px 10px 4px


.tooltip .tooltip-arrow 
  width 0
  height 0
  border-style solid
  position absolute
  margin 5px
  border-color black
  z-index 1
  display none !important


.tooltip[x-placement^="top"] 
  margin-bottom 5px


.tooltip[x-placement^="top"] .tooltip-arrow 
  border-width 5px 5px 0 5px
  border-left-color transparent !important
  border-right-color transparent !important
  border-bottom-color transparent !important
  bottom -5px
  left calc(50% - 5px)
  margin-top 0
  margin-bottom 0


.tooltip[x-placement^="bottom"] 
  // margin-top 100px !important
  border none !important


.tooltip[x-placement^="bottom"] .tooltip-arrow 
  border-width 0 5px 5px 5px
  border-left-color transparent !important
  border-top-color transparent !important
  border-bottom-color transparent !important
  top -5px
  left calc(50% - 5px)
  margin-top 0
  margin-bottom 0
  // display none !important


.tooltip[x-placement^="right"] 
  margin-left 5px


.tooltip[x-placement^="right"] .tooltip-arrow 
  border-width 5px 5px 5px 0
  border-left-color transparent !important
  border-top-color transparent !important
  border-bottom-color transparent !important
  left -5px
  top calc(50% - 5px)
  margin-left 0
  margin-right 0


.tooltip[x-placement^="left"] 
  margin-right 5px


.tooltip[x-placement^="left"] .tooltip-arrow 
  border-width 5px 0 5px 5px
  border-top-color transparent !important
  border-right-color transparent !important
  border-bottom-color transparent !important
  right -5px
  top calc(50% - 5px)
  margin-left 0
  margin-right 0



.tooltip.popover.in-toolbar
  margin-top 55px !important
  
.tooltip.popover
  margin-top 5px
.tooltip.popover .popover-inner 
  background background-color-1
  color black
  // padding 2px 0 10px 0
  padding 0
  border-radius 5px
  box-shadow 0 5px 20px rgba(black, .3)
  text-align center


.tooltip.popover .popover-arrow 
  border-color #f9f9f9


.tooltip[aria-hidden='true'] 
  visibility hidden
  opacity 0
  transition opacity .15s, visibility .15s


.tooltip[aria-hidden='false'] 
  visibility visible
  opacity 1
  transition opacity .15s


.tooltip
  ul
    margin 10px
    padding 0
    list-style none
  li
    list-style none

.tooltip-inner
  .vdp-datepicker__calendar
    border 0 !important

.btn 
  padding 8px 14px
  font-size 16px
  border-radius 5px
  background #fff
  border 1px solid rgba(0,0,0,0.2)
  border-bottom 2px solid rgba(0,0,0,0.25)
  box-shadow 0 2px 6px rgba(0,0,0,.1)
  // margin 8px
  margin-top 8px
  

.btn:hover 
  border-color rgba(0,0,0,.35)
  box-shadow 0 4px 8px rgba(0,0,0,.2)
  background #fff
  cursor pointer



.info-panel
  padding 20px
  position fixed
  right 0
  top 100px
  width 300px
  background #fff
  box-shadow 0 0 30px rgba(0,0,0,.5)
  z-index 10
  
  h2
    font-size 18px

  h3
    font-size 16px
    
  h4
    font-size 14px
    
  pre.code
    overflow scroll
    height 50px

  .ended
    color #999
    
  button
    float right

  ul
    margin 0
    padding 0

  li 
    margin 0 0 6px 0
    font-size 14px
    list-style none
    
  p
    font-size 14px
    margin 0 0 8px 0

.box-with-scrollbar
  max-height 200px
  border 1px solid #ddd
  padding 10px 14px
  border-radius 8px
  overflow scroll      

.hide
  display none

.Timeline-container
  margin-top 0
  // margin-top 30px

.isAutoTrack
  color #f90
  border 1px solid #f90
  padding 3px 8px
  font-size 13px
  display inline-block
  font-weight bold
  
.isAutoPlaylist 
  color #f90
  padding 8px 20px
  border 2px solid #f90
  display inline-block
  font-weight bold

.loading-text
  color #999

.menu-track-edit 
  position fixed
  width 200px
  height 200px
  left 50%
  margin-left -100px
  top 50%
  margin-top -100px
  background #fff
  box-shadow 0 4px 10px rgba(0,0,0,.2)
  z-index 100

.toolbar-track-move
  position fixed
  width 300px
  top 100px
  right 0
  text-align right
  background #fff
  box-shadow 0 4px 10px rgba(0,0,0,.2)
  z-index 100
  padding 20px
  
.track-moving-this
  background #cec !important

.toolbar-track-move button
  float right
  
.button-small
  background #fff
  box-shadow 0 1px 3px rgba(0,0,0,.1)
  border 1px solid rgba(0,0,0,.1)
  border-radius 4px
  padding 4px 7px
  
  &:hover
    border 1px solid rgba(0,0,0,.2)
    box-shadow 0 1px 5px rgba(0,0,0,.2)

.button-block
  background #fff
  box-shadow 0 1px 3px rgba(0,0,0,.1)
  border 1px solid rgba(0,0,0,.1)
  border-radius 4px
  padding 4px 7px
  box-sizing border-box
  display block
  height 100%
  
  &:hover
    border 1px solid rgba(0,0,0,.2)
    box-shadow 0 1px 5px rgba(0,0,0,.2)

.button-large
  background #fff
  box-shadow 0 2px 6px rgba(0,0,0,.1)
  border 2px solid rgba(0,0,0,.1)
  border-radius 6px
  padding 12px 16px
  
  &:hover
    border 2px solid rgba(0,0,0,.2)
    box-shadow 0 1px 5px rgba(0,0,0,.2)


.main-container
  max-width 1200px
  min-width 1000px
  margin 0 auto
  position relative
  // width 100%
  // height 100%
  // background #fff
  // overflow auto

.pages-container
  width 60%
  min-width 600px
  left 0
  top 0
  position absolute
  z-index 2
  
.sidebar-container
  width 40%
  min-width 400px
  left 60%
  // max-height 100%
  position fixed
  // top: 0px;
  // position fixed
  // position absolute
  box-sizing border-box
  padding 30px 14px 14px 14px
  z-index 1
  // overflow auto
  
#Sidebar
  margin 0 40px 100px 0
  padding 1px
  background background-color-1
  box-shadow 0 1px 5px rgba(0,0,0,.2)
  border-radius 8px
  max-width 400px
  // height 110%
  // height 1200px
  
  section
    padding 14px  
    border-bottom 1px solid rgba(0,0,0,.15)
  
    h4
      margin 0 0 12px 0
      text-transform uppercase
      font-size 14px
      opacity 0.6
      font-weight 400

    .current-track
      line-height 150%
      .label
        text-transform uppercase
        margin-right 8px
        opacity 0.4
        font-size 13px
        width 50px
        display inline-block
        text-align left
        font-weight 400
      .artist
        font-size 15px
        margin-right 10px
        font-weight 600
      .title
        font-size 14px

    .message
      background #fff
      box-shadow 0 2px 4px rgba(0,0,0,.2)
      padding 10px
      border-radius 4px
      font-size 14px
      margin 0 0 14px 0

      .text
        color #333
        margin 0 0 10px 0
        line-height 150%
      
      .footer
        
        .from
          margin-right 12px
          color #999
        .date
          color #aaa

    .more-link
      color #777
      display block
      text-decoration none
      font-size 15px
      padding  3px 5px
      // text-align right
      text-align left
      
      &:hover
        color #333
      
      
  section:last-child
    border 0
    
#Timeline
  
  padding 30px

  .segment
    margin 0 0 30px 0

    .segment-row
      display flex
      flex-wrap wrap

      &:first-child
        .segment-content
          border-top-left-radius 6px !important
          border-top-right-radius 6px !important
          overflow hidden
        
      &:last-child
        .segment-content
          border-bottom-right-radius 6px !important
          border-bottom-left-radius 6px !important
          overflow hidden
          
      &:last-child
        border 0
        .segment-track
          .track
            border 0 !important
            
      .segment-time
        width 20%
        text-align right
        
        .segment-time-inner
          font-size 14px
          color rgba(0,0,0,0.6)
          text-align right
          display inline-block

      .segment-content
        padding 0
        width 80%
        background #fff
        box-shadow 0 4px 4px rgba(0,0,0,.2)
        overflow hidden
          
        

    .segment-show
      h3
        font-size 18px
        margin 0 0 10px 0
        
      .segment-time
        .segment-time-inner
          margin-top 10px
          padding 10px 14px 10px 12px
          // padding 8px 14px 8px 12px
          font-size 16px
          color rgba(0,0,0,0.6)
          font-weight 700
          background background-color-1
          border-radius 8px 0 0 8px
          box-shadow 0 4px 4px rgba(0,0,0,.1)
          
      .segment-content
        border-radius 6px 6px 0 0
        border-bottom 1px solid rgba(0,0,0,.1)
        
        .show
          padding 14px 16px

          .label
            float right
            padding 4px 8px 4px 9px
            text-transform uppercase
            font-size 13px
            border-radius 4px
            border 1px solid rgba(0,0,0,.2)
            border-bottom 2px solid rgba(0,0,0,.2)
            color rgba(0,0,0,.6)
            letter-spacing 1px

            &.live
              background #ffb


          .show-image
            max-width 70px
            max-height 70px
            float left
            margin-right 10px
            margin-bottom 14px
            border-radius 4px

          .broadcast-date
            font-size 16px
            color #999

    .segment-track
      
      .track
        border-bottom 1px solid rgba(0,0,0,.1)
        font-size 14px
        overflow auto

        &.status-playing
          background #ffb !important

        &.type-command
          color #000

        &.status-stopped
          background #fff
          box-shadow 0 2px 5px rgba(0,0,0,.1)

        &.status-current
          background #fff
          box-shadow 0 2px 5px rgba(0,0,0,.1)
          z-index 2
          position relative
          .segment-time
            font-weight bold

        &.status-skipped
          background background-color-1
          
          .track-info
            opacity 0.5

        &.status-played
            
          .track-start
            font-weight bold
            color rgba(0,0,0,0.5)
            background background-color-1

        &.is-track-faded
          opacity 0.5

        &.is-track-extra-faded
          opacity 0.25
            
        .track-start
          padding 8px 14px
          float left
          line-height 150%
          min-width 60px
        
        .track-info
          padding 8px 10px
          overflow auto
          line-height 150%
          
          .track-artist
            margin-right 8px

    .segment-row.segment-expand
      
      .segment-content
        background #fff
        
        button
          width 100%
          padding 8px 14px 10px 14px
          text-align center
          border 0
          box-sizing border-box
          font-size 14px
          background background-color-1
          color rgba(0,0,0,0.5)
          
        button:hover
          color #000
          background background-color-1
          cursor pointer
          
      .segment-content
        padding 0

</style>
